import { useState, useEffect } from "react";
import PdfPreview from "../common/viewer/PdfPreview";
import ExcelPreview from "../common/viewer/ExcelPreview";
import { deleteDesignFile } from "../../api/design/file";
import { ChevronRight, ChevronDown } from "lucide-react";
import ForgeDwgViewer from "../dwg/ForgeDwgViewer";
import api from "../../api/axios";

export default function DesignDocDetail({ docDetail }) {
  const [selectedVersion, setSelectedVersion] = useState(null);
  const [open, setOpen] = useState(false);
  const [convertedPdf, setConvertedPdf] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleDeleteFile = async () => {
    if (!selectedVersion?.id) {
      alert("삭제할 파일이 없습니다.");
      return;
    }

    if (!window.confirm("정말 이 파일을 삭제하시겠습니까?")) return;

    try {
      await deleteDesignFile(selectedVersion.id);
      alert("파일이 삭제되었습니다.");
      window.location.reload();
    } catch (err) {
      console.error(err);
      alert("삭제 실패");
    }
  };

  useEffect(() => {
    if (docDetail) {
      if (docDetail.versions?.length > 0) {
        setSelectedVersion(docDetail.versions[0]);
      } else {
        setSelectedVersion({
          file: docDetail.current_file,
          version: docDetail.current_version,
        });
      }
    }
  }, [docDetail]);

  if (!docDetail || !selectedVersion) return null;

  const fileUrl = selectedVersion?.file || docDetail.current_file;
  const ext = fileUrl?.split(".").pop().toLowerCase();

  return (
    <div className="space-y-4">
      {/* 문서 제목 + 분야 + 삭제 버튼 */}
      <div className="space-y-3">
        {/* 제목 + 분야 배지 */}
        <div className="flex items-start gap-2">
          <h3 className="text-base font-semibold text-gray-900 flex-1 min-w-0 break-words">
            {docDetail.name}
          </h3>
          <span className="px-2 py-0.5 text-xs rounded bg-gray-200 text-gray-600 whitespace-nowrap flex-shrink-0">
            {docDetail.discipline_display}
          </span>
        </div>

        {/* 삭제 버튼 */}
        <button
          onClick={handleDeleteFile}
          className="btn-delete-sm"
        >
          삭제
        </button>
      </div>

      {/* 메타데이터 */}
      <div className="text-sm text-gray-600">
        <span className="font-medium text-gray-700">카테고리:</span>{" "}
        <span>{docDetail.category}</span>
      </div>

      {/* 버전 선택 */}
      {docDetail.versions?.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-600 font-medium">버전:</span>
          <select
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm flex-1 min-w-0"
            value={selectedVersion?.id || ""}
            onChange={(e) => {
              const v = docDetail.versions.find(
                (ver) => ver.id == e.target.value
              );
              setSelectedVersion(v);
              setConvertedPdf(null);
            }}
          >
            {docDetail.versions.map((v) => (
              <option key={v.id} value={v.id}>
                {v.version} ({v.uploaded_by.last_name}
                {v.uploaded_by.first_name})
              </option>
            ))}
          </select>
        </div>
      )}

      {/* 변경 로그 */}
      {docDetail.versions?.length > 0 && (
        <div>
          <button
            onClick={() => setOpen(!open)}
            className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2"
          >
            {open ? (
              <ChevronDown size={16} className="text-gray-500" />
            ) : (
              <ChevronRight size={16} className="text-gray-500" />
            )}
            변경 로그
          </button>
          {open && (
            <ul className="text-sm text-gray-600 space-y-2">
              {docDetail.versions.map((v) => (
                <li
                  key={v.id}
                  className="border-b border-gray-200 pb-2"
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0 flex-1">
                      <span className="font-medium">{v.version}</span>
                      <p className="text-xs text-gray-500 truncate">
                        {v.comment || "코멘트 없음"}
                      </p>
                    </div>
                    <span className="text-xs text-gray-500 whitespace-nowrap flex-shrink-0">
                      {v.uploaded_by.last_name}
                      {v.uploaded_by.first_name} ·{" "}
                      {new Date(v.uploaded_at).toLocaleDateString()}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* 미리보기 */}
      <div className="border rounded-lg p-3 bg-gray-50">
        {fileUrl && (
          <div className="flex justify-end mb-2">
            <a
              href={fileUrl}
              download
              target="_blank"
              rel="noreferrer"
              className="btn-basic-sm"
            >
              다운로드
            </a>
          </div>
        )}
        {!fileUrl ? (
          <p className="text-sm text-gray-500">첨부된 파일이 없습니다.</p>
        ) : (
          (() => {
            const supportedPreviewExt = [
              "pdf",
              "doc",
              "docx",
              "xlsx",
              "xls",
              "png",
              "jpg",
              "jpeg",
            ];

            const isSupported = supportedPreviewExt.includes(ext);

            if (!isSupported) {
              return (
                <div className="w-full h-[200px] flex flex-col items-center justify-center bg-gray-200 rounded">
                  <p className="text-gray-700 mb-2 text-sm">
                    이 파일 형식은 미리보기를 지원하지 않습니다.
                  </p>
                  <a
                    href={fileUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 underline text-sm"
                  >
                    파일 다운로드
                  </a>
                </div>
              );
            }

            switch (ext) {
              case "pdf":
                return <PdfPreview fileUrl={fileUrl} />;

              case "doc":
              case "docx":
                return (
                  <iframe
                    src={`https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(fileUrl)}`}
                    className="w-full h-[400px]"
                  />
                );

              case "xlsx":
              case "xls":
                return <ExcelPreview fileUrl={fileUrl} />;

              case "png":
              case "jpg":
              case "jpeg":
                return (
                  <img
                    src={fileUrl}
                    alt={docDetail.name}
                    className="max-h-[400px] mx-auto rounded shadow"
                  />
                );

              default:
                return "미리보기를 지원하지 않는 형식입니다.";
            }
          })()
        )}
      </div>
    </div>
  );
}
